/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

import { LOGIN_PATH } from "../data/constant/path";
import { API_CONFIG } from "../config/api";
import { getAuth, getIdToken, signOut } from "firebase/auth";
import { useUserStore } from "../store/userStore";
import { useStore } from "../store/zustand";

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------
const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    "Content-Type": "application/json",
  },
  
});

// ---------------------------------------------------------------------------
// Retry helper – exponential back-off for network errors & 5xx
// ---------------------------------------------------------------------------
const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

function shouldRetry(error: AxiosError): boolean {
  // Don't retry if we already exhausted attempts
  const config = error.config as InternalAxiosRequestConfig & { _retryCount?: number };
  if (!config) return false;

  const retryCount = config._retryCount ?? 0;
  if (retryCount >= API_CONFIG.MAX_RETRIES) return false;

  // Only retry safe (idempotent) methods
  const method = (config.method ?? "get").toLowerCase();
  if (!(API_CONFIG.RETRYABLE_METHODS as readonly string[]).includes(method)) return false;

  // Retry on network errors (no response)
  if (!error.response) return true;

  // Retry on specific status codes
  return (API_CONFIG.RETRYABLE_STATUS_CODES as readonly number[]).includes(error.response.status);
}

async function retryRequest(error: AxiosError) {
  const config = error.config as InternalAxiosRequestConfig & { _retryCount?: number };
  config._retryCount = (config._retryCount ?? 0) + 1;

  const delay = API_CONFIG.RETRY_DELAY * Math.pow(2, config._retryCount - 1);
  await wait(delay);

  return axiosClient.request(config);
}

// ---------------------------------------------------------------------------
// Request interceptor – attach Firebase token
// ---------------------------------------------------------------------------
axiosClient.interceptors.request.use(
  async function (config) {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      const token = await getIdToken(user);
      config.headers["Authorization"] = "Bearer " + token;
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  },
);

// ---------------------------------------------------------------------------
// Response interceptor – normalise responses & handle errors
// ---------------------------------------------------------------------------
// Runs once even if many in-flight requests fail at the same time (e.g. the
// dashboard fires several calls that all 403 after a block). Without this guard
// each failure would trigger its own redirect.
let forcedLogoutInProgress = false;

/**
 * Clear the session and send the user to the login screen. Signs out of
 * Firebase too — otherwise `onAuthStateChanged` still reports the user as
 * logged in, the route guard lets them back into the app, its requests 403
 * again, and the app bounces to /login in a reload loop. An optional message is
 * stashed for the login screen to show (account blocked/deleted).
 */
export const forceLogout = (message?: string) => {
  if (forcedLogoutInProgress) return;
  forcedLogoutInProgress = true;

  try {
    localStorage.removeItem("token");
    localStorage.removeItem("userDetails");
    // Reset the in-memory stores too: persist() writes surviving state back on
    // the next set(), leaking the previous user's details into the next session.
    useUserStore.getState().resetUserDetails();
    useStore.getState().logout();
    if (message) {
      sessionStorage.setItem(
        "authNotice",
        JSON.stringify({ type: "error", message }),
      );
    }
  } catch {
    // storage unavailable (e.g. private mode) — continue.
  }

  // Redirect only after Firebase state is actually cleared, so the login screen
  // doesn't re-evaluate auth as "still signed in" and bounce back.
  signOut(getAuth())
    .catch(() => {})
    .finally(() => {
      window.location.replace(LOGIN_PATH);
    });
};

axiosClient.interceptors.response.use(
  function (response) {
    return response.data;
  },
  async function (error: AxiosError) {
    // --- Retry logic ---
    if (shouldRetry(error)) {
      return retryRequest(error);
    }

    // --- Special cases ---
    // Treat 423 as success for lock/unlock
    if (
      error.response?.status === 423 &&
      error.config?.url?.includes("LockUnlockResponse")
    ) {
      return error.response.data;
    }

    // 401 – session expired
    if (error.response?.status === 401) {
      forceLogout();
      return Promise.reject(error);
    }

    // 403 – account blocked/deleted by an admin (enforced server-side on every
    // request). Clear the session, stash a notice for the login screen to show,
    // and redirect. Any other 403 falls through to the caller's own handling.
    if (error.response?.status === 403) {
      const code = (error.response.data as { code?: string } | undefined)?.code;
      if (code === "ACCOUNT_BLOCKED" || code === "ACCOUNT_DELETED") {
        forceLogout(
          code === "ACCOUNT_BLOCKED"
            ? "Your account has been blocked by an administrator. Please contact support."
            : "This account no longer exists.",
        );
        return Promise.reject(error);
      }
    }

    // --- Timeout ---
    if (error.code === "ECONNABORTED") {
      const timeoutError = new Error(
        "Request timed out. Please check your connection and try again.",
      );
      (timeoutError as any).isTimeout = true;
      return Promise.reject(timeoutError);
    }

    // --- Network error (offline / DNS failure) ---
    if (!error.response) {
      const networkError = new Error(
        "Unable to reach the server. Please check your internet connection.",
      );
      (networkError as any).isNetworkError = true;
      return Promise.reject(networkError);
    }

    // --- Rate limited ---
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers["retry-after"];
      const msg = retryAfter
        ? `Too many requests. Please wait ${retryAfter} seconds.`
        : "Too many requests. Please try again shortly.";
      const rateLimitError = new Error(msg);
      (rateLimitError as any).isRateLimited = true;
      return Promise.reject(rateLimitError);
    }

    return Promise.reject(error);
  },
);

export default axiosClient;
